// ============================================================================
// TEMPORARY DEV-ONLY NAVIGATION OVERRIDE
// ============================================================================
// Lets FBP/Fairbooks screens be reached + tested while the backend navigation
// catalog (module → menu → screen + role permissions) is not yet seeded. The
// real menu and role-based access will come from the common BMS / Syut Admin
// module; when that ships, set DEV_NAV_ENABLED = false (or delete this file and
// its merge in login-interface.tsx) — a no-op swap (ADR-0008).
//
// This does NOT read/write iam.tbl_* directly (Syut rule 1). It only appends to
// the frontend NAVIGATION_MENU / MODULES_CONFIG built at login. `allowedActions`
// grants the test user full access to each screen for now; real per-role actions
// arrive with BMS/Syut Admin.

// Flipped to false 2026-07-02 after seeding all 5 modules / 16 screens / 3
// roles into the tenant DB (see fairbooks-db/seed/_dev/900_seed_dev_rbac_test.sql).
// Sidebar is now driven entirely from /me/navigation. Set to true only when
// re-testing on a fresh tenant that has no seed applied yet.
export const DEV_NAV_ENABLED = false;

// ----------------------------------------------------------------------------
// TEMPORARY DEV-ONLY SCOPE (ENTITY) FALLBACK
// ----------------------------------------------------------------------------
// Entity-scoped endpoints (coa_group / coa_ledger ...) require an X-Entity-ID
// header. That header is normally populated from the login scope
// (scopeConfiguration.accessibleEntities → selected_entity_id). While the
// org/bms entity master is not yet seeded, the Syut tenant returns no
// accessible entities, so the header is empty and the API responds 400
// "Entity context is required". This fallback supplies a fixed dev entity id
// when none is selected, so the screens can be tested. It is a no-op once a
// real entity is selected (the interceptor prefers the selected entity). The
// id is just a scoping value — accounts schema has no FK to an org/entity
// table — so any stable UUID works. Remove (or set DEV_SCOPE_ENABLED = false)
// when org/bms supplies real entities.
// Flipped to false 2026-07-02 after seeding real entities + BUs. AuthContext
// auto-picks the user's primary entity/BU from /me/permissions and persists it
// to localStorage — the axios interceptor now uses that as the X-Entity-ID /
// X-Business-Unit-ID header source. The stub UUIDs below are retained only for
// SalesInvoiceScreen + ServiceMasterScreen which still reference DEV_BU.id in
// their buDescriptions logic (TODO: replace those with SELECTED_BU_ID reads).
export const DEV_SCOPE_ENABLED = false;
export const DEV_ENTITY = { id: '00000000-0000-0000-0000-0000000000e1', name: 'Dev Entity' };
export const DEV_BU     = { id: '00000000-0000-0000-0000-0000000000b1', name: 'Main' };

export interface DevNavMenu {
  /** Menu code (used as the menu id / MODULES_CONFIG key). */
  menuCode: string;
  menuName: string;
  /** IconSystem key (camelCase). */
  icon: string;
  /** Must match a key in COMPONENT_REGISTRY. */
  primaryScreenCode: string;
  allowedActions: string[];
}

export interface DevNavModule {
  moduleName: string;
  icon: string;
  menus: DevNavMenu[];
}

export const DEV_NAV_MODULES: DevNavModule[] = [
  {
    moduleName: 'Accounts',
    icon: 'folder',
    menus: [
      {
        menuCode: 'ACCOUNTS_GROUP',
        menuName: 'Account Groups',
        icon: 'folder',
        primaryScreenCode: 'AccountGroupsScreen',
        allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'],
      },
      {
        menuCode: 'ACCOUNTS_LEDGER',
        menuName: 'Ledger Accounts',
        icon: 'list',
        primaryScreenCode: 'LedgerAccountsScreen',
        allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'],
      },
      {
        menuCode: 'ACCOUNTS_VOUCHER_TYPE',
        menuName: 'Voucher Types',
        icon: 'file',
        primaryScreenCode: 'VoucherTypesScreen',
        allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'],
      },
      {
        menuCode: 'ACCOUNTS_JOURNAL',
        menuName: 'Journal Entry',
        icon: 'edit',
        primaryScreenCode: 'JournalEntryScreen',
        allowedActions: ['VIEW', 'CREATE'],
      },
      {
        menuCode: 'ACCOUNTS_ENTITY_CONFIG',
        menuName: 'Entity Accounting Config',
        icon: 'settings',
        primaryScreenCode: 'EntityAccountingConfigScreen',
        allowedActions: ['VIEW', 'UPDATE'],
      },
      {
        menuCode: 'ACCOUNTS_PERIOD_LOCK',
        menuName: 'Period Locks',
        icon: 'settings',
        primaryScreenCode: 'PeriodLockScreen',
        allowedActions: ['VIEW', 'CREATE', 'DELETE'],
      },
    ],
  },
  {
    moduleName: 'Commercial',
    icon: 'folder',
    menus: [
      { menuCode: 'PARTY_CLIENT', menuName: 'Clients', icon: 'user', primaryScreenCode: 'ClientMasterScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'SALES_SERVICE_CATEGORY', menuName: 'Service Categories', icon: 'folder', primaryScreenCode: 'ServiceCategoryScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'SALES_SERVICE', menuName: 'Services', icon: 'file', primaryScreenCode: 'ServiceMasterScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'SALES_INVOICE', menuName: 'Service Invoices', icon: 'file', primaryScreenCode: 'SalesInvoiceScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'CREDIT_NOTE', menuName: 'Credit / Debit Notes', icon: 'file', primaryScreenCode: 'CreditNoteScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'CUSTOMER_SETOFF', menuName: 'Customer Set-off', icon: 'file', primaryScreenCode: 'CustomerSetoffScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'PURCHASE_INVOICE', menuName: 'Purchase Invoices', icon: 'file', primaryScreenCode: 'PurchaseInvoiceScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'PURCHASE_DEBIT_NOTE', menuName: 'Purchase Debit Notes', icon: 'file', primaryScreenCode: 'PurchaseDebitNoteScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'SUPPLIER_SETOFF', menuName: 'Supplier Set-off', icon: 'file', primaryScreenCode: 'SupplierSetoffScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
    ],
  },
  {
    moduleName: 'Treasury',
    icon: 'dollarSign',
    menus: [
      {
        menuCode: 'MENU_RECEIPT',
        menuName: 'Receipts',
        icon: 'dollarSign',
        primaryScreenCode: 'ReceiptScreen',
        allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'EXPORT'],
      },
      {
        menuCode: 'MENU_RECEIPT_WORKSHEET',
        menuName: 'Receipt Verification Worksheet',
        icon: 'check',
        primaryScreenCode: 'ReceiptWorksheetScreen',
        allowedActions: ['VIEW', 'UPDATE', 'APPROVE', 'REJECT', 'EXPORT'],
      },
    ],
  },
  {
    // ADR-0055 §1: bank statements + reconciliation, separate from Treasury.
    moduleName: 'Banking',
    icon: 'link',
    menus: [
      {
        menuCode: 'MENU_BANK_STATEMENT',
        menuName: 'Bank Statements',
        icon: 'file',
        primaryScreenCode: 'BankStatementScreen',
        allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE', 'IMPORT', 'EXPORT'],
      },
      {
        menuCode: 'MENU_BANK_RECONCILIATION',
        menuName: 'Bank Reconciliation',
        icon: 'link',
        primaryScreenCode: 'BankReconciliationScreen',
        allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE', 'EXPORT'],
      },
    ],
  },
  {
    moduleName: 'Tax',
    icon: 'settings',
    menus: [
      { menuCode: 'TAX_CONFIG', menuName: 'Tax Settings', icon: 'settings', primaryScreenCode: 'TaxSettingsScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
    ],
  },
  {
    moduleName: 'Settings',
    icon: 'settings',
    menus: [
      { menuCode: 'MENU_ORGANISATION', menuName: 'Organisation', icon: 'folder', primaryScreenCode: 'OrganisationScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE'] },
      { menuCode: 'MENU_ROLE_PERMISSIONS', menuName: 'Roles & Permissions', icon: 'lock', primaryScreenCode: 'RolePermissionsScreen', allowedActions: ['VIEW', 'CREATE', 'UPDATE', 'DELETE'] },
      { menuCode: 'MENU_USER_ACCESS', menuName: 'User Access', icon: 'users', primaryScreenCode: 'UserAccessScreen', allowedActions: ['VIEW', 'UPDATE'] },
      { menuCode: 'MENU_ACCOUNT_TYPES', menuName: 'Account Types', icon: 'folder', primaryScreenCode: 'AccountTypesScreen', allowedActions: ['VIEW'] },
      { menuCode: 'MENU_ACCOUNT_SUBTYPES', menuName: 'Account Subtypes', icon: 'folder', primaryScreenCode: 'AccountSubtypesScreen', allowedActions: ['VIEW'] },
    ],
  },
  {
    moduleName: 'Reports',
    icon: 'list',
    menus: [
      {
        menuCode: 'MENU_TRIAL_BALANCE',
        menuName: 'Trial Balance',
        icon: 'chart',
        primaryScreenCode: 'TrialBalanceScreen',
        allowedActions: ['VIEW', 'EXPORT'],
      },
      {
        menuCode: 'ACCOUNTS_REPORT_DAYBOOK',
        menuName: 'Day Book',
        icon: 'list',
        primaryScreenCode: 'DayBookScreen',
        allowedActions: ['VIEW'],
      },
      {
        menuCode: 'ACCOUNTS_REPORT_LEDGER',
        menuName: 'Ledger',
        icon: 'file',
        primaryScreenCode: 'LedgerStatementScreen',
        allowedActions: ['VIEW'],
      },
      {
        menuCode: 'ACCOUNTS_REPORT_OPENBILLS',
        menuName: 'Open Bills',
        icon: 'list',
        primaryScreenCode: 'OpenBillReportScreen',
        allowedActions: ['VIEW'],
      },
      {
        menuCode: 'ACCOUNTS_REPORT_DEBTORS_AGEING',
        menuName: 'Debtors Ageing',
        icon: 'clock',
        primaryScreenCode: 'DebtorsAgeingScreen',
        allowedActions: ['VIEW', 'EXPORT'],
      },
    ],
  },
];
